/**
 * ตั้งค่าพื้นที่ออฟฟิศ (GPS) — เมนูแยกสำหรับ HR/Owner จัดการ geofence ได้ตรงๆ
 * โดยไม่ต้องเข้าหน้าตั้งค่าองค์กร. ใช้ settings key/value ชุดเดียวกับหน้า Settings:
 * โหลดค่า "ทั้งก้อน" มาไว้ใน form แล้วเซฟทั้งก้อน (POST /settings เป็น per-key upsert
 * — ไม่ทับ key อื่นที่ไม่ได้แตะ). reuse GeofenceModeSelect / OfficeLocationSuggestionCard
 * / Field จากหน้า Settings เพื่อให้พฤติกรรมตรงกันเป๊ะ.
 */
import { useEffect, useState } from 'react';
import { Save, MapPin } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { useGeofenceSettings, useUpdateGeofenceSettings } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { Field, GeofenceModeSelect, OfficeLocationSuggestionCard, type FieldDef } from '../owner/Settings';

type State = Record<string, string>;

const GEOFENCE_FIELDS: FieldDef[] = [
  { key: 'GEOFENCE_ENABLED', label: 'ตรวจพิกัดตอนเช็คอิน', type: 'checkbox', hint: 'ปิด = เช็คอินจากที่ไหนก็ผ่าน (เหมาะกับงานนอกสถานที่/หลายสาขา)' },
  { key: 'OFFICE_LATITUDE', label: 'Latitude', type: 'text', hint: 'เช่น 13.7563' },
  { key: 'OFFICE_LONGITUDE', label: 'Longitude', type: 'text', hint: 'เช่น 100.5018' },
  { key: 'OFFICE_RADIUS_METERS', label: 'รัศมีที่อนุญาต (เมตร)', type: 'number', min: '20', max: '5000', hint: 'เริ่มต้น 200 — ต่ำกว่า 20 ม. เสี่ยงสแกนไม่ผ่านเพราะ GPS คลาดเคลื่อน' },
];

export function OfficeGeofenceSettings() {
  const { data, isLoading, isError, error, refetch } = useGeofenceSettings();
  const update = useUpdateGeofenceSettings();
  const toast = useToast();
  const [form, setForm] = useState<State>({});
  const [saved, setSaved] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  function setField(key: string, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  function handleUseCurrentLocation() {
    if (!navigator.geolocation) {
      toast.error('อุปกรณ์นี้ไม่รองรับการอ่านตำแหน่ง');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setField('OFFICE_LATITUDE', pos.coords.latitude.toFixed(6));
        setField('OFFICE_LONGITUDE', pos.coords.longitude.toFixed(6));
        setGeoLoading(false);
      },
      (err) => {
        const msg =
          err.code === err.PERMISSION_DENIED ? 'ไม่ได้รับอนุญาตให้เข้าถึงตำแหน่ง — เปิดสิทธิ์ตำแหน่งในเบราว์เซอร์แล้วลองใหม่' :
          err.code === err.POSITION_UNAVAILABLE ? 'หาตำแหน่งไม่ได้ ลองใหม่อีกครั้ง' :
          err.code === err.TIMEOUT ? 'อ่านตำแหน่งนานเกินไป ลองใหม่อีกครั้ง' :
          'อ่านตำแหน่งไม่สำเร็จ';
        toast.error(msg);
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  async function handleSave() {
    // ตรวจรัศมีก่อนส่ง — ค่าผิดทำให้ทั้งฟอร์มเซฟไม่ได้ (server reject)
    const raw = form.OFFICE_RADIUS_METERS;
    if (raw !== undefined && raw !== '') {
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 20 || n > 5000) {
        toast.error('ค่าในช่อง "รัศมีที่อนุญาต (เมตร)" ไม่ถูกต้อง (ต้องอยู่ระหว่าง 20–5000)');
        return;
      }
    }
    try {
      await update.mutateAsync(form);
      setSaved(true);
      toast.success('บันทึกการตั้งค่าแล้ว');
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  if (isError) {
    return (
      <AdminLayout title="ตั้งค่าพื้นที่ออฟฟิศ (GPS)">
        <div className="card p-4 text-center">
          <div className="text-sm text-red-600 mb-3">
            โหลดการตั้งค่าไม่สำเร็จ — {error instanceof Error ? error.message : 'เกิดข้อผิดพลาด'}
          </div>
          <button onClick={() => refetch()} className="btn-secondary text-sm">
            ลองใหม่
          </button>
        </div>
      </AdminLayout>
    );
  }

  if (isLoading || !data) {
    return (
      <AdminLayout title="ตั้งค่าพื้นที่ออฟฟิศ (GPS)">
        <Loading />
      </AdminLayout>
    );
  }

  const isPlaceholder =
    form.GEOFENCE_ENABLED === 'true' && form.OFFICE_LATITUDE === '13.7563' && form.OFFICE_LONGITUDE === '100.5018';

  return (
    <AdminLayout title="ตั้งค่าพื้นที่ออฟฟิศ (GPS)">
      <div className="card p-4 mb-3">
        <div className="font-semibold mb-3">พื้นที่ออฟฟิศ (geofence)</div>
        <GeofenceModeSelect
          value={(form.GEOFENCE_MODE as 'off' | 'observe' | 'enforce' | undefined) ?? 'observe'}
          onChange={(v) => setField('GEOFENCE_MODE', v)}
        />
        <div className="space-y-3">
          {GEOFENCE_FIELDS.map((f) => (
            <Field key={f.key} f={f} value={form[f.key] ?? ''} onChange={(v) => setField(f.key, v)} />
          ))}
        </div>
        <button
          type="button"
          onClick={handleUseCurrentLocation}
          disabled={geoLoading}
          className="btn-secondary text-sm mt-3 w-full justify-center"
        >
          <MapPin className="w-4 h-4" /> {geoLoading ? 'กำลังอ่านตำแหน่ง…' : '📍 ใช้ตำแหน่งปัจจุบัน'}
        </button>
        {isPlaceholder && (
          <div className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2 mt-3">
            ⚠️ พิกัดนี้เป็นค่าตัวอย่าง ไม่ใช่ที่ตั้งออฟฟิศจริง — พนักงานที่สแกนเข้างานจะถูกตีว่าอยู่นอกพื้นที่และต้องรอ HR ตรวจสอบทุกครั้ง กดปุ่มใช้ตำแหน่งปัจจุบันขณะอยู่ที่ออฟฟิศเพื่อแก้
          </div>
        )}
      </div>

      <OfficeLocationSuggestionCard
        onApplied={(lat, lng, radiusM) => {
          setField('OFFICE_LATITUDE', lat.toFixed(6));
          setField('OFFICE_LONGITUDE', lng.toFixed(6));
          setField('OFFICE_RADIUS_METERS', String(radiusM));
          setField('GEOFENCE_MODE', 'enforce');
        }}
      />

      <button onClick={handleSave} disabled={update.isPending} className="btn-primary w-full">
        <Save className="w-5 h-5" />
        {update.isPending ? 'กำลังบันทึก…' : saved ? 'บันทึกแล้ว ✓' : 'บันทึก'}
      </button>
    </AdminLayout>
  );
}
